import { reaction } from 'mobx';

import { IRootStore } from 'shared/entities/store/rootStore';
import {
  mapScenarioUpdateErrorToMessage,
  ScenarioBaseParamsServer,
  ScenarioCommonServer,
  ScenarioPreset,
  ScenarioUpdateError
} from 'shared/entities/scenario';
import { apiUrls } from 'shared/entities/domain';
import { ISmartbotAIStore } from 'shared/entities/store/smartbotAIStore';
import { LoadingStageModel } from 'shared/models/loadingStage';
import { FieldArrayModel, FieldModel } from 'shared/models/form';
import { IAIBotScenarioData } from 'shared/entities/smartbotAI';
import { Bucket } from 'shared/entities/bucket';
import { ReactionsHandlerStore } from 'stores/reactionsHandlerStore';
import { OpenStateModel } from 'shared/models/openState';
import { localStorageHandler } from 'stores/localStorageHandler';
import { createFirstConnectionKey } from 'shared/entities/localStorage';
import getBooleanValueFromLS from 'shared/utils/getBooleanValueFromLS';

import { createSmartbotAITag } from './utils';

export class SmartbotAIConnectionStore extends ReactionsHandlerStore {
  readonly rootStore: IRootStore;
  readonly smartbotAIStore: ISmartbotAIStore;
  readonly createScenarioStage: LoadingStageModel = new LoadingStageModel();
  readonly selectedScenarioId: FieldModel<string | null>;
  readonly scenarioChannelsUpdatingStage: LoadingStageModel =
    new LoadingStageModel();
  readonly selectedChannelIds: FieldArrayModel<string> =
    new FieldArrayModel<string>([]);

  readonly botTestingModalState: OpenStateModel = new OpenStateModel();

  constructor({
    rootStore,
    smartbotAIStore
  }: {
    rootStore: IRootStore;
    smartbotAIStore: ISmartbotAIStore;
  }) {
    super();
    this.rootStore = rootStore;
    this.smartbotAIStore = smartbotAIStore;
    this.selectedScenarioId = new FieldModel<string | null>(
      smartbotAIStore.bot.scenarios.isEmpty
        ? null
        : smartbotAIStore.bot.scenarios.keys[0]
    );
  }

  get scenarios() {
    return this.smartbotAIStore.bot.scenarios;
  }

  get selectedScenario(): IAIBotScenarioData | null {
    if (!this.selectedScenarioId.value) {
      return null;
    }

    return this.scenarios.getEntity(this.selectedScenarioId.value);
  }

  get scenarioChannelIds(): string[] {
    if (!this.selectedScenario) {
      return [];
    }

    const channelIds = this.selectedScenario.channelIds.value;
    if (channelIds === null) {
      return this.rootStore.channelsStore.channels.keys;
    }

    return channelIds.filter((channelId) =>
      this.rootStore.channelsStore.channels.keys.includes(channelId)
    );
  }

  get connectButtonDisabled(): boolean {
    return (
      !this.selectedChannelIds.touched ||
      this.scenarioChannelsUpdatingStage.isLoading
    );
  }

  get maybeIsFirstConnection(): boolean {
    return (
      this.rootStore.smartbotAIBotsStore.bots.length === 1 &&
      this.scenarios.length === 1
    );
  }

  get isChannelsAdded(): boolean {
    const oldIds = this.scenarioChannelIds;
    const newIds = this.selectedChannelIds.value;

    const isSame =
      oldIds.length === newIds.length &&
      oldIds.every((id) => newIds.includes(id));

    const isAdded = newIds.some((id) => !oldIds.includes(id));

    return isSame || isAdded;
  }

  initialize = () => {
    this.initializeReactions();
    this.initializeScenarios();
    this.initializeChannels();
  };

  getSelectedChannel = (channelId: string): boolean => {
    return this.selectedChannelIds.value.includes(channelId);
  };

  selectChannel = ({
    value,
    channelId
  }: {
    value: boolean;
    channelId: string;
  }) => {
    if (value) {
      this.selectedChannelIds.changeValue([
        ...this.selectedChannelIds.value,
        channelId
      ]);
    } else {
      this.selectedChannelIds.changeValue(
        this.selectedChannelIds.value.filter((item) => item != channelId)
      );
    }
  };

  updateScenarioChannelIds = async () => {
    if (
      this.scenarioChannelsUpdatingStage.isLoading ||
      !this.selectedScenarioId.value ||
      !this.selectedScenario ||
      !this.smartbotAIStore.bot.id.value
    ) {
      return {
        isError: true
      };
    }

    this.scenarioChannelsUpdatingStage.loading();

    const response = await this.rootStore.networkStore.api<
      {
        scenario: ScenarioBaseParamsServer;
      },
      ScenarioUpdateError
    >(apiUrls.SCENARIOS_UPDATE, {
      method: 'POST',
      data: {
        channel_ids: this.selectedChannelIds.value,
        _id: this.selectedScenarioId.value,
        bucket: Bucket.dev
      },
      showExpectedError: false,
      errorsMap: mapScenarioUpdateErrorToMessage
    });

    if (!response.isError) {
      this.selectedScenario.channelIds.changeValue(
        response.data.scenario.channel_ids
      );

      const bot = this.rootStore.smartbotAIBotsStore.bots.getEntity(
        this.smartbotAIStore.bot.id.value
      );

      bot?.scenarios.items.forEach((scenario) => {
        if (scenario.id === this.selectedScenarioId.value) {
          scenario.channelIds.changeValue(response.data.scenario.channel_ids);
        }
      });

      this.rootStore.channelsStore.load();

      this.scenarioChannelsUpdatingStage.success();
    } else {
      this.scenarioChannelsUpdatingStage.error();
    }

    return {
      isError: response.isError
    };
  };

  getIsFirstConnection = (): boolean => {
    return this.maybeIsFirstConnection && !this.scenarioChannelIds.length;
  };

  saveIsFirstConnectionBeforePayment = (value: boolean) => {
    if (!this.rootStore.projectId) {
      return;
    }

    localStorageHandler.setItem(
      createFirstConnectionKey(
        this.rootStore.projectId,
        this.smartbotAIStore.bot.id.value
      ),
      value.toString()
    );
  };

  getIsFirstConnectionBeforePayment = (): boolean => {
    if (!this.rootStore.projectId) {
      return false;
    }

    return (
      getBooleanValueFromLS(
        createFirstConnectionKey(
          this.rootStore.projectId,
          this.smartbotAIStore.bot.id.value
        )
      ) ?? false
    );
  };

  handleBalanceOpen = () => {
    if (!this.rootStore.projectId || !this.rootStore.paymentStore.info) {
      return;
    }

    if (
      this.getIsFirstConnectionBeforePayment() &&
      this.rootStore.paymentStore.info.billing.balance > 0
    ) {
      this.botTestingModalState.open();
      localStorageHandler.remove(
        createFirstConnectionKey(
          this.rootStore.projectId,
          this.smartbotAIStore.bot.id.value
        )
      );
    }
  };

  unsubscribe(): void {
    this.disposeAll();
  }

  private createScenario = async (): Promise<
    BaseResponse<ScenarioCommonServer>
  > => {
    const projectId = this.rootStore.projectId;

    if (!projectId || this.createScenarioStage.isLoading) {
      return {
        isError: true
      };
    }

    this.createScenarioStage.loading();

    const response = await this.rootStore.networkStore.api<{
      scenario: ScenarioCommonServer;
    }>(apiUrls.TEMPLATES_APPLY, {
      method: 'POST',
      data: {
        preset: ScenarioPreset.smartbotAI,
        publish: true,
        params: {
          tags: [createSmartbotAITag(projectId)],
          ai_bot_id: this.smartbotAIStore.bot.id.value,
          channel_ids: [] // Не подключать к каналам при создании
        }
      }
    });

    if (response.isError) {
      this.createScenarioStage.error();
      return {
        isError: true
      };
    }

    this.createScenarioStage.success();

    return {
      isError: false,
      data: response.data.scenario
    };
  };

  private initializeChannels = () => {
    this.selectedChannelIds.changeValue([...this.scenarioChannelIds]);
    this.selectedChannelIds.resetTouched();
  };

  private initializeScenarios = async (): Promise<void> => {
    if (this.scenarios.keys.length) {
      return;
    }

    const response = await this.createScenario();

    if (response.isError) {
      return;
    }

    const normalizer = (raw) => ({
      key: raw._id,
      entity: {
        name: response.data.name,
        id: response.data._id,
        blockId: '',
        channelIds: new FieldModel([])
      }
    });

    this.scenarios.fillByRawData({
      raw: [response.data],
      normalizer
    });

    this.rootStore.smartbotAIBotsStore.bots
      .getEntity(this.smartbotAIStore.bot.id.value)
      ?.scenarios.fillByRawData({
        raw: [response.data],
        normalizer
      });

    this.selectedScenarioId.changeValue(response.data._id);
  };

  private initializeReactions = (): void => {
    this.addReaction({
      key: 'scenarioChannelIds',
      reaction: reaction(
        () => this.scenarioChannelIds,
        () => {
          this.selectedChannelIds.changeValue([...this.scenarioChannelIds]);
          this.selectedChannelIds.resetTouched();
        }
      )
    });
  };
}
